import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Clock, User, CalendarCheck2 } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import ContactForm from "../components/ContactForm";
import { BlogRequestState } from "../components/BlogRequestState";
import { useBlogPost } from "../hooks/useBlogPost";
import { COMPANY, formatDate, breadcrumbLd, SITE_URL } from "../data/site";

// minimal markdown-ish renderer for our seeded content
function renderContent(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let list = null;
  let table = null;
  let para = [];

  const flushPara = () => {
    if (para.length) {
      const text = para.join(" ");
      out.push(<p key={out.length} dangerouslySetInnerHTML={{ __html: inline(text) }} />);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      out.push(<ul key={out.length}>{list.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}</ul>);
      list = null;
    }
  };
  const flushTable = () => {
    if (table) {
      out.push(
        <table key={out.length}>
          <thead><tr>{table.header.map((h, i) => <th key={i}>{h.trim()}</th>)}</tr></thead>
          <tbody>{table.rows.map((row, i) => <tr key={i}>{row.map((c, j) => <td key={j} dangerouslySetInnerHTML={{ __html: inline(c.trim()) }} />)}</tr>)}</tbody>
        </table>
      );
      table = null;
    }
  };

  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");

  for (let raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      flushPara(); flushList(); flushTable();
      out.push(<h2 key={out.length}>{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      flushPara(); flushList(); flushTable();
      out.push(<h3 key={out.length}>{line.slice(4)}</h3>);
    } else if (/^\s*[-*]\s+/.test(line)) {
      flushPara(); flushTable();
      list = list || [];
      list.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (line.startsWith("|") && line.endsWith("|")) {
      flushPara(); flushList();
      const cells = line.slice(1, -1).split("|");
      if (!table) {
        table = { header: cells, rows: [], divider: false };
      } else if (!table.divider) {
        // 2nd row = divider like |---|---|
        table.divider = true;
      } else {
        table.rows.push(cells);
      }
    } else if (line.trim() === "") {
      flushPara(); flushList(); flushTable();
    } else {
      para.push(line);
    }
  }
  flushPara(); flushList(); flushTable();
  return out;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { post, loading, error, retry } = useBlogPost(slug);
  if (!post) return <BlogRequestState loading={loading} error={error} retry={retry} />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.cover_image,
      url: `${SITE_URL}/blog/${post.slug}`,
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
      author: { "@type": "Organization", name: post.author, url: `${SITE_URL}/about` },
      publisher: {
        "@type": "Organization",
        name: COMPANY.name,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/tsb-logo.jpg` },
      },
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      keywords: post.keywords.join(", "),
    },
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
  ];

  return (
    <>
      <Seo
        title={post.seo_title || post.title}
        description={post.excerpt}
        canonical={`${SITE_URL}/blog/${post.slug}`}
        type="article"
        image={post.cover_image}
        publishedTime={post.published_at}
        modifiedTime={post.updated_at || post.published_at}
        jsonLd={jsonLd}
      />

      <article className="bg-white pt-32 pb-20">
        <header className="max-w-3xl mx-auto px-5 lg:px-8">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Blog", path: "/blog" },
              { name: post.title, path: `/blog/${post.slug}` },
            ]}
          />
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-bold text-stone-600 hover:text-red-600"
            data-testid="blog-post-back"
          >
            <ArrowLeft size={14} /> All articles
          </Link>
          <div className="mt-8 text-[10px] uppercase tracking-[0.22em] font-bold text-red-600">{post.category}</div>
          <h1 data-testid="blog-post-title" className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-stone-900 leading-[1.02]">
            {post.title}
          </h1>
          <p className="mt-6 text-xl text-stone-600 leading-relaxed">{post.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-500">
            <span className="flex items-center gap-1.5"><User size={14} /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {post.read_time}</span>
            <span className="flex items-center gap-1.5" data-testid="blog-post-published">
              Published <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
            </span>
            <span className="flex items-center gap-1.5 font-semibold text-stone-700" data-testid="blog-post-updated">
              <CalendarCheck2 size={14} className="text-red-600" /> Updated{" "}
              <time dateTime={post.updated_at || post.published_at}>{formatDate(post.updated_at || post.published_at)}</time>
            </span>
          </div>
        </header>

        <figure className="max-w-5xl mx-auto px-5 lg:px-8 mt-12">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover border border-stone-200"
          />
        </figure>

        {post.content_format === "html" ? (
          <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-14 prose-tsb overflow-x-auto" data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-14 prose-tsb overflow-x-auto" data-testid="blog-post-content">
            {renderContent(post.content)}
          </div>
        )}

        <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-16 p-8 bg-stone-900 text-white">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-500">Free estimate</div>
          <div className="mt-3 font-display text-2xl font-bold tracking-tight">
            Project in mind? We pour or plant within 5–10 business days.
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="btn-primary" data-testid="blog-post-estimate-link">Get a Free Estimate</Link>
            <a href={COMPANY.phoneTel} className="btn-ghost-dark" data-testid="blog-post-call-link">
              <Phone size={16} /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </article>

      <ContactForm compact />
    </>
  );
}
